/**
 * Auth helpers — login and session bootstrap on top of lib/api.ts.
 *
 * The access token lives in module memory (see api.ts), not localStorage.
 * This module owns the login POST and the boot-time session check.
 */

import type { User, PinLoginResponse } from "@eagledispatch/shared";
import {
  apiFetch,
  setSession,
  bootstrapSession,
  logout as clearSession,
  type SessionUser,
} from "./api";
import { fireCookieClear } from "./cookie-flush";

interface LoginResponse {
  user?: User;
  accessToken?: string;
  requiresWebAuthn?: boolean;
  challengeToken?: string;
  options?: Record<string, unknown>;
}

export type LoginResult =
  | { requiresWebAuthn: false; user: User }
  | {
      requiresWebAuthn: true;
      challengeToken: string;
      options: Record<string, unknown>;
    };

export interface DriverLoginResult {
  user: User;
  driverId: string;
}

/**
 * POST /auth/login (username + password — DISPATCHER / OFFICE).
 * If 2FA is required, returns { requiresWebAuthn: true, challengeToken, options }.
 * Otherwise stores the session in memory and returns { requiresWebAuthn: false, user }.
 */
export async function login(
  username: string,
  password: string,
  opts?: { fallback?: boolean; tagUid?: string },
): Promise<LoginResult> {
  const body = await apiFetch<LoginResponse>("/api/v1/auth/login", {
    method: "POST",
    body: JSON.stringify({
      username,
      password,
      // SPEC-68 §3.5 — only send the key when true; an absent key means
      // "no change" server-side and keeps the request byte-identical to today.
      // `fallback: false` must NEVER be sent (§4.5c: false is identical to
      // absent server-side, and the strict no-change contract is easier to
      // audit when the wire never carries it).
      ...(opts?.fallback ? { fallback: true } : {}),
      // SPEC-70 §2.7 — the presence factor. Only sent when a scan produced a
      // UID; an absent key keeps the request byte-identical to the pre-gate
      // wire (passkey-ux 47a pins the exact-body shapes).
      ...(opts?.tagUid ? { tagUid: opts.tagUid } : {}),
    }),
  });

  if (body.requiresWebAuthn && body.challengeToken && body.options) {
    return {
      requiresWebAuthn: true,
      challengeToken: body.challengeToken,
      options: body.options,
    };
  }

  if (body.user && body.accessToken) {
    setSession(body.user, body.accessToken);
    return { requiresWebAuthn: false, user: body.user };
  }

  throw new Error("Invalid response from server");
}

/**
 * POST /auth/login/webauthn/finish — Step 2 WebAuthn assertion completion.
 * On success stores the session in memory and returns the user.
 */
export async function finishWebauthnLogin(
  challengeToken: string,
  response: Record<string, unknown>,
): Promise<User> {
  const body = await apiFetch<{
    user: User;
    accessToken: string;
    driverId: string | null;
  }>(
    "/api/v1/auth/login/webauthn/finish",
    {
      method: "POST",
      body: JSON.stringify({ challengeToken, response }),
    },
  );
  // SPEC-68 §4.5f — the driver id rides the body AND the access token. Fold it
  // onto the session user so a passkey sign-in routes to DriverApp exactly
  // like a PIN sign-in does (DriverApp reads session.user.driverId).
  setSession(
    body.driverId ? { ...body.user, driverId: body.driverId } : body.user,
    body.accessToken,
  );
  return body.user;
}

/**
 * POST /auth/pin-login (DRIVER — userId + 4-digit PIN).
 * On success stores the session and returns the user + driverId.
 */
export async function pinLogin(
  userId: string,
  pin: string,
  tagUid?: string,
): Promise<DriverLoginResult> {
  const body = await apiFetch<PinLoginResponse>("/api/v1/auth/pin-login", {
    method: "POST",
    body: JSON.stringify({
      userId,
      pin,
      // SPEC-70 §2.7 — the presence factor. Omitted entirely when no scan
      // produced a UID, keeping the pre-gate wire shape byte-identical.
      ...(tagUid ? { tagUid } : {}),
    }),
  });
  setSession(body.user, body.accessToken);
  return { user: body.user, driverId: body.driverId };
}

/** Restore a session from the refresh cookie on app boot. */
export async function restoreSession(): Promise<SessionUser | null> {
  const session = await bootstrapSession();
  return session ? session.user : null;
}

/**
 * POST /auth/logout then clear the in-memory session. Best-effort.
 *
 * SPEC-71 §3C / BUG #5 — this function needs NO `intentional` flag of its own:
 * it delegates the local clear to `clearSession()` (api.ts's `logout`), which
 * fires `notifyUnauthorized(undefined, true)`. The intentional classification
 * is owned by api.ts (the only place that calls `notifyUnauthorized`), so a
 * user-initiated sign-out / Clock Out completes with no kick toast and never
 * raises the durable login line.
 */
export async function logout(): Promise<void> {
  // SPEC-72 Phase B2: an explicit logout must clear the Keychain vault, or
  // the B1 re-seed resurrects the session at next launch (logout stays
  // logout). Fired BEFORE the POST: the vault clear is best-effort and
  // synchronous (postMessage), so it runs even when the network POST fails
  // or hangs — local sign-out must never depend on the network.
  fireCookieClear();
  try {
    await apiFetch<{ ok: boolean }>("/api/v1/auth/logout", { method: "POST" });
  } catch {
    // Logout is best-effort: even if the network drops, clear locally.
  }
  clearSession();
}
